<template>
	<div class="flex">
		<div
			v-if="subCategories?.length > 0"
			class="hidden lg:block pt-10 px-8 basis-[28%] bg-gray-100"
		>
			<h2 v-if="parentCategory" class="mb-8">
				<NuxtLink :to="getCategoryLink(parentCategory)">
					{{ parentCategory?.Name }}
				</NuxtLink>
			</h2>
			<CategoryNavigation
				:sub-categories="subCategories || []"
				class="mt-4 grid gap-[28px]"
			/>
		</div>
		<div
			class="lg:pt-8 pb-8 lg:px-5 shrink xl:max-w-[75rem] max-w-full mx-auto"
			:class="subCategories?.length > 0 ? 'lg:basis-3/4' : 'lg:basis-full'"
		>
			<div class="lg:mb-0 mb-8">
				<div v-if="currentCategory?.ImagePath" class="relative mb-6 pb-[52%]">
					<NuxtImg
						:src="currentCategory.ImagePath"
						:alt="currentCategory?.Name"
						:class="`object-cover object-center absolute w-full h-full`"
					/>
				</div>
				<div class="mb-5">
					<div>
						<LayoutBreadcrumbs class="!mt-0 md:pt-5 lg:pt-0" />

						<h1 class="mb-2 text-[27px] font-semibold">
							{{ currentCategory?.Name }}
						</h1>
					</div>
					<div v-if="storyData?.top_blocks?.length > 0" class="grid gap-4 mb-4">
						<BlokPriorityScope
							v-for="(blok, index) in storyData.top_blocks"
							:key="blok._uid"
							:priority="index <= priorityIndex"
						>
							<StoryblokComponent :blok="blok" />
						</BlokPriorityScope>
					</div>
					<div
						v-if="safeDescription"
						class="rich-text text-[15px] leading-[1.6] font-sans"
						v-html="safeDescription"
					/>
				</div>
				<ProductRecommendations
					v-if="currentCategory?.Level === 1 && recommendations"
					:blok="recommendations"
					:hierarchies="[[currentCategory.Name]]"
					title-styles="block text-lg text-center font-bold pb-5 border-b border-gray-300"
					class="mb-5 grid grid-cols-1"
				/>
			</div>

			<div v-if="!storyData?.hide_products">
				<div
					v-if="isSidebar"
					class="mb-4 flex flex-wrap justify-between items-center"
				>
					<Sidebar
						:filters="filters"
						:initial-selected-filters="selectedFilters.selectedFilters"
						@send-selected-filters="sendSelectedFilters"
					/>
					<div
						class="md:ml-auto md:mr-2 mb-2 max-w-[48%] md:max-w-[200px] w-full inline-flex items-center justify-end text-primary text-sm font-medium gap-6"
					>
						<ElSelect
							v-model="currentSortingOrder"
							name="sortingOrder"
							class="w-full text-gray-700"
							:options="sortingOrdersOptions"
							btn-styles="!pr-0 flex items-center justify-center h-[52px] md:ml-auto md:!w-[200px] border border-black rounded-3xl"
							btn-wrap-styles="w-full"
						/>
					</div>
					<p
						class="md:mt-0 mt-4 mb-2 md:w-auto w-full md:text-left text-center"
					>
						{{ itemCount }} {{ $t("Products") }}
					</p>
				</div>
				<ProductListing
					:items="combinedItems"
					:item-count="itemCount"
					:page-size="currentPageSize"
					:picture-aspect-ratio="pageConfiguration.pictureAspectRatio"
					:current-page="
						pageConfiguration.showPagination
							? currentPage
							: paginationCurrentPage
					"
					:is-loaded="isLoaded"
					:special-price="specialPrice"
					:category-name="currentCategory?.Name"
					:show-pagination="pageConfiguration.showPagination"
					class="mb-10"
					@get-current-page-products="getCurrentPageProducts"
				/>
				<LazyLoadWrapper>
					<div v-if="itemCount">
						<StoryblokComponent
							v-for="blok in preFooter"
							:key="blok._uid"
							:blok="blok"
						/>
					</div>
				</LazyLoadWrapper>
			</div>

			<div v-if="storyData?.bottom_blocks?.length > 0" class="grid gap-4">
				<StoryblokComponent
					v-for="blok in storyData.bottom_blocks"
					:key="blok._uid"
					:blok="blok"
				/>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { lastPriorityIndex } from "~/layers/base/utils/imagePriority";
import { sanitizeHtml } from "~/layers/base/utils/html";
import { computed, ref, toRef } from "vue";
import type {
	CreateFilterText,
	FilteredProductItem
} from "@nwt/norce-pwa/dist/runtime/types/api/searchAndFilter/filters";
import type { Category } from "@nwt/norce-pwa/dist/runtime/types/api/category/category";
import ProductListing from "./ProductListing.vue";
import Sidebar from "./Sidebar.vue";
import ElSelect from "~/templates/gotaplatsensfoto/components/Elements/ElSelect.vue";
import LazyLoadWrapper from "~/components/LazyLoadWrapper.vue";
import type {
	CombinedItem,
	InitialData,
	PageConfiguration
} from "~/types/components/category";
import type {
	BlokColumns,
	SpecialPrice,
	CategoryPage,
	AdvertisementBlok,
	BlokProductRecommendations
} from "~/types/storyblok/storyblok";

import { getFiltersFromUrl } from "~/utils/categories/getFiltersFromUrl";
import { CategoryUrlParams, activeStatusID } from "~/utils/constants";
import {
	DEFAULT_PRODUCT_SORT,
	PRODUCT_SORT_OPTIONS
} from "~/utils/product/sort";
import type { GetFilteredProducts } from "@nwt/norce-pwa/dist/runtime/types/api/searchAndFilter/searchAndFilterParams";

interface Props {
	currentCategory: Category;
	initialData: InitialData;
	preFooter: BlokColumns[];
	storyData?: CategoryPage;
	specialPrice?: SpecialPrice | null;
	/** Category pages; the base brand page passes the listing props below. */
	pageConfiguration?: PageConfiguration;
	pageSize?: string;
	pictureAspectRatio?: string;
	initialCurrentSortingOrder?: string;
	/** Brand pages: `{ manufacturerSeed }`. */
	productsPopulationConfig?: Record<string, string | number>;
	advertisement?: AdvertisementBlok;
	recommendations?: BlokProductRecommendations;
	initialPageNo?: number;
}

const {
	currentCategory,
	initialData,
	preFooter,
	storyData,
	specialPrice = null,
	pageConfiguration: categoryPageConfiguration,
	pageSize = "12",
	pictureAspectRatio = "4/3",
	initialCurrentSortingOrder = DEFAULT_PRODUCT_SORT,
	productsPopulationConfig,
	advertisement,
	initialPageNo = 1
} = defineProps<Props>();

const pageConfiguration: PageConfiguration = categoryPageConfiguration ?? {
	pageSize,
	pictureAspectRatio,
	initialCurrentSortingOrder,
	showPagination: false,
	rootParentCode: null
};
const listingSeed = productsPopulationConfig?.manufacturerSeed
	? productsPopulationConfig
	: { categorySeed: currentCategory?.CategoryId?.toString() };

const priorityIndex = computed(() => lastPriorityIndex(storyData?.top_blocks));
const safeDescription = computed(() =>
	sanitizeHtml(currentCategory?.Description)
);

const { listProductByFilters, createFilterText } = useNorcePwa();
const norceStore = useNorceStore();
const { fetchAllParametrics } = useAllParametrics();
const { t, locale } = useI18n();
const route = useRoute();
const { createCategoryLink } = useCategory();
const categoryStore = useCategoryStore();
const { helloRetailTrack } = useHelloRetailTracking();

const initialDataRef = toRef(() => initialData);

const sortingOrdersOptions = computed(() =>
	PRODUCT_SORT_OPTIONS.map(({ labelKey, value }) => ({
		label: t(labelKey),
		value
	}))
);
const currentSortingOrder = ref(pageConfiguration.initialCurrentSortingOrder);
const currentPageSize = ref(pageConfiguration.pageSize);
const filters = ref(initialDataRef.value.filters);
const selectedFilters = ref<CreateFilterText>(
	!route.query.filter
		? { allParametrics: [], selectedFilters: {} }
		: getFiltersFromUrl(route.query.filter as string)
);
const currentPage = ref(
	pageConfiguration.showPagination && route.query.pageNo
		? Number(route.query.pageNo)
		: 1
);

const buildFetchParams = (
	filterText: string,
	page: number
): GetFilteredProducts => {
	return {
		...listingSeed,
		filter: filterText,
		sort: currentSortingOrder.value,
		pageNo: String(page),
		pageSize: currentPageSize.value,
		statusSeed: String(activeStatusID),
		cultureCode: norceStore.cultureCode
	};
};

const fetchPage = async (page: number) => {
	const filterText = await createFilterText(selectedFilters.value);
	return listProductByFilters(buildFetchParams(filterText, page));
};

const {
	currentPage: paginationCurrentPage,
	currentItems,
	itemCount,
	isLoaded,
	resetToFirstPage,
	applyResults,
	syncUrl
} = useCategoryPagination({
	initialPageNo,
	initialItems:
		(initialDataRef.value.products?.Items as FilteredProductItem[]) ?? [],
	initialItemCount: initialDataRef.value.products?.ItemCount ?? 0,
	pageSize: currentPageSize,
	fetchPage
});

const combinedItems = computed<CombinedItem[]>(() => {
	const advertisementConfig = storyData?.advertisement
		? storyData.advertisement[0]
		: advertisement;
	const products = currentItems.value || [];
	const result: CombinedItem[] = products.map(
		(product) =>
			({
				type: "product" as const,
				id: `${product.Id}`,
				data: product
			}) as CombinedItem
	);

	if (!advertisementConfig) {
		return result;
	}

	const positions =
		advertisementConfig.items_position?.split(",").map(Number) || [];
	const fullWidthItems = advertisementConfig?.items_width
		? advertisementConfig.items_width.split(",")
		: [];
	advertisementConfig.items.forEach((adItem, index) => {
		const position = positions[index] ? positions[index] - 1 : -1;
		const ad = {
			type: "advertisement" as const,
			id: adItem._uid,
			data: adItem,
			isFullWidth: fullWidthItems.includes(`${index}`)
		} as CombinedItem;

		if (position >= 0 && position <= result.length) {
			result.splice(position, 0, ad);
		} else {
			result.push(ad);
		}
	});

	return result;
});

const getCategoryLink = (category: Category) => {
	return `/${createCategoryLink({ category })}`;
};

const buildQueryParams = (
	params: GetFilteredProducts,
	urlPage: number
): Record<string, string> => {
	const queryParams: Record<string, string> = Object.fromEntries(
		Object.entries(params)
			.filter(Boolean)
			.filter(
				([key, value]) =>
					value &&
					(key === CategoryUrlParams.FILTER ||
						(key === CategoryUrlParams.SORT && value !== DEFAULT_PRODUCT_SORT))
			)
	);
	if (pageConfiguration.showPagination && urlPage > 1) {
		queryParams.pageNo = urlPage.toString();
	} else if (!pageConfiguration.showPagination && urlPage > 1) {
		queryParams.p = urlPage.toString();
	}
	return queryParams;
};

// Shared by the filter sidebar, the sort select and the paginator. Only the
// paginator keeps the page: a filter or sort change must collapse to page 1,
// or the old page number overshoots the new result set and the grid is empty.
const sendSelectedFilters = async (
	ref: CreateFilterText,
	options?: { keepPage?: boolean }
) => {
	isLoaded.value = false;
	resetToFirstPage();
	if (!options?.keepPage) {
		currentPage.value = 1;
	}

	ref.allParametrics = await fetchAllParametrics();
	const filterText = await createFilterText(ref);

	const urlPage = pageConfiguration.showPagination ? currentPage.value : 1;
	const params = buildFetchParams(filterText, urlPage);
	syncUrl(buildQueryParams(params, urlPage));

	selectedFilters.value = ref;
	const data = await listProductByFilters(params);

	applyResults(data);

	isLoaded.value = true;
};

const isSidebar = computed(
	() =>
		Object.keys(selectedFilters.value.selectedFilters).length > 0 ||
		itemCount.value > 0
);
const parentCategory = computed(() => {
	return currentCategory?.Level !== 1 && currentCategory?.ParentId
		? categoryStore.getCategoryByParam(
				"Id",
				currentCategory.ParentId,
				locale.value
			)
		: currentCategory;
});
const rootCategory = computed(() => {
	return pageConfiguration.rootParentCode
		? categoryStore.getCategoryByParam(
				"Code",
				pageConfiguration.rootParentCode,
				locale.value
			)
		: null;
});
const subCategories = computed(() => {
	return currentCategory?.Level !== 1
		? rootCategory.value?.Children
		: currentCategory?.Children;
});

const getCurrentPageProducts = async (page: number) => {
	currentPage.value = page;
	await sendSelectedFilters(selectedFilters.value, { keepPage: true });
};

watch(currentSortingOrder, async () => {
	await sendSelectedFilters(selectedFilters.value);
});

onMounted(() => {
	helloRetailTrack({
		serviceType: "pageview",
		trackingData: {
			location: window.location.href,
			referrer: window.location.origin,
			url: window.location.href
		}
	});
});
</script>
